import type {
  ConstructorGrid,
  Pairing,
  PairingStatus,
  PlacementOption,
  Word,
  ClueStatus,
} from '../types'
import {
  evaluatePlacementCandidate,
  validatePlacementBatch,
  findPlacementOptions,
  getUsedWordIds,
  type PlacePairInput,
  type EvaluatePlacementResult,
} from './constructor'
import { generateId, getLetters } from './pairings'

// =============================================================================
// Backward tree search at arbitrary depth
// =============================================================================
//
// Given a target pair T that doesn't fit on the grid, find chains of helper
// placements [h1, h2, ..., hN] that, applied in order, make T fit.
//
// Apply order: h1 is applied first (it must fit the bare grid), then h2 (now
// fits because h1 is on the grid), …, finally T.
//
// Each step in the chain SHARES A CELL with the next — by construction, not by
// post-hoc filter. We pick h_k specifically because its placement crosses an
// empty cell of h_{k+1} (or T) at exactly the letter that one needs.

export type HelperStep = {
  wordA: Word
  wordB: Word
  pairing: Pairing | null   // null = virtual; resolved on apply
  position: PlacementOption
}

export type Solution = {
  helpers: HelperStep[]      // apply order
  target: PlacementOption
}

export type BackwardsSearchFilters = {
  statusFilters?: ReadonlyArray<PairingStatus>
  clueFilters?: ReadonlyArray<ClueStatus | 'empty'>
  matchesSearch?: ((lettersA: string, lettersB: string) => boolean) | undefined
}

export type BackwardsSearchOptions = {
  // Cap helpers considered per "needed cell" of the target (or sub-target).
  // When omitted, picks a dynamic cap based on depth (shallower → bigger cap)
  // since deep searches fan out exponentially in this number.
  maxBridgesPerCell?: number | undefined
  maxSolutionsTotal?: number | undefined
  // Hard wall-clock budget (ms). The search aborts and returns partial
  // results once exceeded — keeps depth-3 from locking up the page.
  timeBudgetMs?: number | undefined
  filters?: BackwardsSearchFilters | undefined
}

export type BackwardsSearchResult = {
  solutions: Solution[]
  // True when the search returned partial results because the time budget was
  // hit. UI can show a "more might exist" indicator.
  truncated: boolean
}

const STATUS_RANK: Record<PairingStatus, number> = {
  approved: 0,
  draft: 1,
  none: 2,
  rejected: 3,
}

// -----------------------------------------------------------------------------
// Helper pool / index
// -----------------------------------------------------------------------------

function helperPassesFilters(
  pairing: Pairing | null,
  lettersA: string,
  lettersB: string,
  filters: BackwardsSearchFilters | undefined,
): boolean {
  if (!filters) {
    if (pairing && pairing.status === 'rejected') return false
    return true
  }
  const { statusFilters, clueFilters, matchesSearch } = filters

  if (statusFilters && statusFilters.length > 0) {
    const status: PairingStatus = pairing ? pairing.status : 'none'
    if (!statusFilters.includes(status)) return false
  } else if (pairing && pairing.status === 'rejected') {
    return false
  }

  if (clueFilters && clueFilters.length > 0) {
    const clueState: ClueStatus | 'empty' = pairing?.clue?.text ? pairing.clue.status : 'empty'
    if (!clueFilters.includes(clueState)) return false
  }

  if (matchesSearch && !matchesSearch(lettersA, lettersB)) return false

  return true
}

type HelperCandidate = {
  wordA: Word
  wordB: Word
  pairing: Pairing | null
  lettersA: string
  lettersB: string
}

function buildEligibleHelperPool(
  grid: ConstructorGrid,
  words: Word[],
  pairings: Pairing[],
  excludeWordAId: string,
  excludeWordBId: string,
  filters: BackwardsSearchFilters | undefined,
): HelperCandidate[] {
  // Mirror the suggestion-system rule: helpers can't reuse a word that's already
  // somewhere on the grid (or that's a variant of one — getUsedWordIds expands
  // group memberships).
  const usedWordIds = getUsedWordIds(grid, pairings, words)

  const wordsA = words.filter(w =>
    w.list === 'A' && w.id !== excludeWordAId && !usedWordIds.has(w.id),
  )
  const wordsB = words.filter(w =>
    w.list === 'B' && w.id !== excludeWordBId && !usedWordIds.has(w.id),
  )

  const pairingMap = new Map<string, Pairing>()
  for (const p of pairings) pairingMap.set(`${p.wordAId}:${p.wordBId}`, p)

  const out: HelperCandidate[] = []
  for (const a of wordsA) {
    const lettersA = getLetters(a)
    for (const b of wordsB) {
      const lettersB = getLetters(b)
      if (lettersA.length !== lettersB.length || lettersA.length === 0) continue
      const pairing = pairingMap.get(`${a.id}:${b.id}`) ?? null
      if (!helperPassesFilters(pairing, lettersA, lettersB, filters)) continue
      out.push({ wordA: a, wordB: b, pairing, lettersA, lettersB })
    }
  }
  return out
}

type HelperIndex = Map<string, Array<{ helper: HelperCandidate; letterIndex: number }>>

function buildHelperIndex(pool: HelperCandidate[]): HelperIndex {
  const index: HelperIndex = new Map()
  for (const helper of pool) {
    for (let i = 0; i < helper.lettersA.length; i++) {
      const a = helper.lettersA[i]
      const b = helper.lettersB[i]
      if (!a || !b) continue
      const key = `${a}:${b}`
      const list = index.get(key)
      const entry = { helper, letterIndex: i }
      if (list) list.push(entry)
      else index.set(key, [entry])
    }
  }
  return index
}

// -----------------------------------------------------------------------------
// Cell helpers
// -----------------------------------------------------------------------------

function placementCells(
  row: number,
  col: number,
  length: number,
  direction: 'across' | 'down',
): Array<{ row: number; col: number }> {
  const cells: Array<{ row: number; col: number }> = []
  for (let i = 0; i < length; i++) {
    if (direction === 'across') cells.push({ row, col: col + i })
    else cells.push({ row: row + i, col })
  }
  return cells
}

function cellIsEmpty(grid: ConstructorGrid, row: number, col: number): boolean {
  if (row < 0 || row >= grid.height || col < 0 || col >= grid.width) return false
  const cell = grid.cells[row]?.[col]
  if (!cell) return false
  if (cell.isBlocked) return false
  const aReal = cell.letterA !== null && cell.letterA !== '□'
  const bReal = cell.letterB !== null && cell.letterB !== '□'
  return !aReal && !bReal
}

// -----------------------------------------------------------------------------
// Phase 1: target candidate positions (fits structurally but lacks intersection
// — i.e. could land here if any one cell were filled by a helper).
// -----------------------------------------------------------------------------

function findTargetCandidatePositions(
  grid: ConstructorGrid,
  targetA: string,
  targetB: string,
): PlacementOption[] {
  const out: PlacementOption[] = []
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      for (const direction of ['across', 'down'] as const) {
        const evalT = evaluatePlacementCandidate(grid, row, col, direction, targetA, targetB)
        if (!evalT.fits) continue
        if (evalT.rawIntersectionCount > 0) continue   // already in findPlacementOptions
        out.push({ row, col, direction, intersectionCount: 0 })
      }
    }
  }
  return out
}

// -----------------------------------------------------------------------------
// Bridge enumeration: find helper pairs that can sit perpendicular to a
// "thing" we're trying to place, with their letter at the right index landing
// in a specified cell.
// -----------------------------------------------------------------------------

type BridgeAttempt = {
  helper: HelperCandidate
  startRow: number
  startCol: number
  direction: 'across' | 'down'
  evalResult: EvaluatePlacementResult
}

function* bridgeCell(
  grid: ConstructorGrid,
  cell: { row: number; col: number },
  neededA: string,
  neededB: string,
  parallelDirection: 'across' | 'down',
  helperIndex: HelperIndex,
  usedWordIds: Set<string>,
  maxBridges: number,
): Generator<BridgeAttempt> {
  const matches = helperIndex.get(`${neededA}:${neededB}`) ?? []
  const helperDirection: 'across' | 'down' = parallelDirection === 'across' ? 'down' : 'across'
  let yielded = 0
  const seen = new Set<string>()

  for (const { helper, letterIndex } of matches) {
    if (yielded >= maxBridges) break
    // Skip helpers that would re-use a word already on the chain (target's
    // words OR a previously-picked helper's words). Same word twice on the
    // grid is invalid; same-word filter must run at every recursion level,
    // not just once on the initial pool.
    if (usedWordIds.has(helper.wordA.id) || usedWordIds.has(helper.wordB.id)) continue
    const startRow = helperDirection === 'down' ? cell.row - letterIndex : cell.row
    const startCol = helperDirection === 'across' ? cell.col - letterIndex : cell.col
    const dedupeKey = `${helper.wordA.id}:${helper.wordB.id}@${startRow},${startCol},${helperDirection}`
    if (seen.has(dedupeKey)) continue
    seen.add(dedupeKey)

    const evalResult = evaluatePlacementCandidate(
      grid,
      startRow,
      startCol,
      helperDirection,
      helper.lettersA,
      helper.lettersB,
    )
    yield { helper, startRow, startCol, direction: helperDirection, evalResult }
    yielded++
  }
}

function helperStepFrom(attempt: BridgeAttempt): HelperStep {
  return {
    wordA: attempt.helper.wordA,
    wordB: attempt.helper.wordB,
    pairing: attempt.helper.pairing,
    position: {
      row: attempt.startRow,
      col: attempt.startCol,
      direction: attempt.direction,
      intersectionCount: attempt.evalResult.validIntersectionCount,
    },
  }
}

function helperStepToInput(step: HelperStep): PlacePairInput {
  return {
    pairingId: step.pairing?.id ?? null,
    wordAText: step.wordA.text,
    wordBText: step.wordB.text,
    row: step.position.row,
    col: step.position.col,
    direction: step.position.direction,
    id: generateId(),
  }
}

// -----------------------------------------------------------------------------
// Recursive chain finder. Given a "thing to place" (letters at a position),
// find chains of EXACTLY `depthRemaining` helpers that, applied first, make
// the thing fit.
//
// Apply order in returned chains: h1 first (fits bare grid), … hN last (fits
// once previous helpers are applied). The "thing" is then placed on top of
// the chain.
// -----------------------------------------------------------------------------

type EnumerateContext = {
  grid: ConstructorGrid
  helperIndex: HelperIndex
  maxBridgesPerCell: number
  // Cap on solutions emitted across the whole search; we yield-and-check.
  remainingBudget: { value: number }
  // Wall-clock deadline (ms since epoch). Past this, aborted is set true and
  // generators return early.
  deadline: number
  aborted: { value: boolean }
}

function isExpired(ctx: EnumerateContext): boolean {
  if (ctx.aborted.value) return true
  if (performance.now() > ctx.deadline) {
    ctx.aborted.value = true
    return true
  }
  return false
}

function chainKey(chain: HelperStep[]): string {
  return chain
    .map(s => `${s.wordA.id}:${s.wordB.id}@${s.position.row},${s.position.col},${s.position.direction}`)
    .join('|')
}

function* enumerateChainsExact(
  ctx: EnumerateContext,
  letters: { A: string; B: string },
  position: PlacementOption,
  exactDepth: number,
  usedWordIds: Set<string>,
): Generator<HelperStep[]> {
  if (ctx.remainingBudget.value <= 0) return
  if (isExpired(ctx)) return

  // Does (letters @ position) fit on the bare grid right now?
  const evalBare = evaluatePlacementCandidate(
    ctx.grid,
    position.row,
    position.col,
    position.direction,
    letters.A,
    letters.B,
  )
  if (!evalBare.fits) return  // unfixable (conflict / adjacency / OOB / blocked)

  const fitsAlready =
    ctx.grid.placements.length === 0 || evalBare.rawIntersectionCount > 0

  // Base case: thing fits with NO helpers. Only yield if exactDepth == 0.
  if (fitsAlready) {
    if (exactDepth === 0) yield []
    return
  }

  // Doesn't fit bare. Need at least one helper, so exactDepth must be ≥ 1.
  if (exactDepth <= 0) return

  // Find a bridge for one of the empty cells; recurse to build the inner chain.
  const cells = placementCells(position.row, position.col, letters.A.length, position.direction)
  for (let j = 0; j < cells.length; j++) {
    if (ctx.remainingBudget.value <= 0 || isExpired(ctx)) return
    const cell = cells[j]!
    if (!cellIsEmpty(ctx.grid, cell.row, cell.col)) continue
    const neededA = letters.A[j]
    const neededB = letters.B[j]
    if (!neededA || !neededB) continue

    for (const attempt of bridgeCell(
      ctx.grid,
      cell,
      neededA,
      neededB,
      position.direction,
      ctx.helperIndex,
      usedWordIds,
      ctx.maxBridgesPerCell,
    )) {
      if (ctx.remainingBudget.value <= 0 || isExpired(ctx)) return
      const helperStep = helperStepFrom(attempt)
      // Extend the used-words set for the recursion: anything below us in the
      // chain can't reuse this helper's words either.
      const newUsedWordIds = new Set(usedWordIds)
      newUsedWordIds.add(attempt.helper.wordA.id)
      newUsedWordIds.add(attempt.helper.wordB.id)

      // Recurse: how do we make THIS helper fit with depth-1 helpers under it?
      for (const innerChain of enumerateChainsExact(
        ctx,
        { A: attempt.helper.lettersA, B: attempt.helper.lettersB },
        helperStep.position,
        exactDepth - 1,
        newUsedWordIds,
      )) {
        if (isExpired(ctx)) return
        const fullChain = [...innerChain, helperStep]
        // Sanity-check: applying fullChain to bare grid produces a state where
        // (letters @ position) actually fits. The recursion's invariants
        // should guarantee this, but a final validatePlacementBatch here
        // catches any subtle interaction (e.g. the inner helper's placement
        // creating an adjacency conflict for the outer one).
        const apply = validatePlacementBatch(ctx.grid, fullChain.map(helperStepToInput))
        if (!apply.valid) continue
        const evalAfter = evaluatePlacementCandidate(
          apply.grid,
          position.row,
          position.col,
          position.direction,
          letters.A,
          letters.B,
        )
        if (!evalAfter.fits || evalAfter.validIntersectionCount === 0) continue

        yield fullChain
      }
    }
  }
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Find chains of EXACTLY `depth` helpers that make the target pair fit.
 *
 * - depth = 1: one helper bridges the target's empty cell and fits the grid.
 * - depth = 2: two helpers — first one fits the grid, second bridges the
 *              target. The first helper bridges an empty cell of the second.
 * - depth = N: as above, generalised.
 *
 * Each step in the returned chain is connected by construction (it bridges
 * the next), so chains are always meaningful — no disconnected helpers.
 *
 * Same-pair re-use within a single chain is forbidden (would be a useless
 * loop).
 */
export function findSolutionsAtDepth(
  grid: ConstructorGrid,
  targetWordA: Word,
  targetWordB: Word,
  words: Word[],
  pairings: Pairing[],
  depth: number,
  options: BackwardsSearchOptions = {},
): Solution[] {
  return findSolutionsAtDepthDetailed(grid, targetWordA, targetWordB, words, pairings, depth, options).solutions
}

// Same as findSolutionsAtDepth but exposes whether the time budget was hit so
// the UI can mark results as "more might exist". Use this for any caller
// that wants to surface that state.
export function findSolutionsAtDepthDetailed(
  grid: ConstructorGrid,
  targetWordA: Word,
  targetWordB: Word,
  words: Word[],
  pairings: Pairing[],
  depth: number,
  options: BackwardsSearchOptions = {},
): BackwardsSearchResult {
  if (depth < 1 || depth > 6) return { solutions: [], truncated: false }

  // Bridge fan-out drops sharply with depth — depth 3 with bridges=12 explodes
  // (12³ = 1728 candidates per target cell, each one validated). Default to
  // shallower caps for deeper searches; caller can override.
  const defaultBridges = depth === 1 ? 12 : depth === 2 ? 8 : 5
  const {
    maxBridgesPerCell = defaultBridges,
    maxSolutionsTotal = depth >= 3 ? 12 : 25,
    timeBudgetMs = 1500,
    filters,
  } = options

  const targetA = getLetters(targetWordA)
  const targetB = getLetters(targetWordB)
  if (targetA.length === 0 || targetA.length !== targetB.length) return { solutions: [], truncated: false }

  const candidates = findTargetCandidatePositions(grid, targetA, targetB)
  if (candidates.length === 0) return { solutions: [], truncated: false }

  const pool = buildEligibleHelperPool(
    grid,
    words,
    pairings,
    targetWordA.id,
    targetWordB.id,
    filters,
  )
  if (pool.length === 0) return { solutions: [], truncated: false }
  const helperIndex = buildHelperIndex(pool)

  // Initial used-word set: target's own two words (and the recursion adds each
  // helper's two words as the chain grows). Grid-occupied words are already
  // filtered out by buildEligibleHelperPool.
  const initialUsedWordIds = new Set<string>([targetWordA.id, targetWordB.id])

  const ctx: EnumerateContext = {
    grid,
    helperIndex,
    maxBridgesPerCell,
    remainingBudget: { value: maxSolutionsTotal * 6 },
    deadline: performance.now() + timeBudgetMs,
    aborted: { value: false },
  }

  const solutions: Solution[] = []
  const seen = new Set<string>()

  for (const candidate of candidates) {
    if (ctx.remainingBudget.value <= 0 || isExpired(ctx)) break
    if (solutions.length >= maxSolutionsTotal * 4) break

    for (const chain of enumerateChainsExact(
      ctx,
      { A: targetA, B: targetB },
      candidate,
      depth,
      initialUsedWordIds,
    )) {
      if (isExpired(ctx)) break
      ctx.remainingBudget.value--
      // Final apply, get the target's actual intersection count under the
      // applied chain (for ranking and display).
      const apply = validatePlacementBatch(ctx.grid, chain.map(helperStepToInput))
      if (!apply.valid) continue
      const evalAfter = evaluatePlacementCandidate(
        apply.grid,
        candidate.row,
        candidate.col,
        candidate.direction,
        targetA,
        targetB,
      )
      if (!evalAfter.fits || evalAfter.validIntersectionCount === 0) continue

      const targetPos: PlacementOption = {
        row: candidate.row,
        col: candidate.col,
        direction: candidate.direction,
        intersectionCount: evalAfter.validIntersectionCount,
      }

      const key = chainKey(chain) + `=>${targetPos.row},${targetPos.col},${targetPos.direction}`
      if (seen.has(key)) continue
      seen.add(key)
      solutions.push({ helpers: chain, target: targetPos })
      if (solutions.length >= maxSolutionsTotal * 4) break
    }
  }

  return {
    solutions: rankSolutions(solutions).slice(0, maxSolutionsTotal),
    truncated: ctx.aborted.value,
  }
}

function rankSolutions(solutions: Solution[]): Solution[] {
  return [...solutions].sort((a, b) => {
    // 1) Direct placements (0 helpers) ALWAYS rank above any chain — they
    //    don't require placing other things first, so they're strictly
    //    cheaper to apply.
    const aDirect = a.helpers.length === 0 ? 0 : 1
    const bDirect = b.helpers.length === 0 ? 0 : 1
    if (aDirect !== bDirect) return aDirect - bDirect

    // 2) Higher target intersection count = more constrained / better fit.
    if (a.target.intersectionCount !== b.target.intersectionCount) {
      return b.target.intersectionCount - a.target.intersectionCount
    }

    // 3) Shorter chains preferred (fewer prerequisites).
    if (a.helpers.length !== b.helpers.length) return a.helpers.length - b.helpers.length

    // 4) Best helper status in the chain (used > approved > favourite > none).
    const statusA = a.helpers[0]?.pairing?.status ?? 'none'
    const statusB = b.helpers[0]?.pairing?.status ?? 'none'
    if (STATUS_RANK[statusA] !== STATUS_RANK[statusB]) return STATUS_RANK[statusA] - STATUS_RANK[statusB]

    if (a.target.row !== b.target.row) return a.target.row - b.target.row
    return a.target.col - b.target.col
  })
}

/**
 * Unified ranked list of placement options for a target pair.
 * Mixes "direct placements" (0 helpers, target fits the bare grid with at
 * least one intersection) with backward-search chains (1..maxDepth helpers
 * that bridge the target's empty cells). All ranked together so the user
 * picks the best option without thinking about the depth bucket.
 */
export function findUnifiedSolutions(
  grid: ConstructorGrid,
  targetWordA: Word,
  targetWordB: Word,
  words: Word[],
  pairings: Pairing[],
  pairingId: string | null,
  maxDepth: number,
  options: BackwardsSearchOptions = {},
): BackwardsSearchResult {
  const collected: Solution[] = []
  let truncated = false

  // Depth 0 — direct placements (handled by findPlacementOptions, which
  // already applies adjacency / wildcard / ownership rules).
  const directOpts = pairingId !== null
    ? { pairingId, maxResults: 50 }
    : { maxResults: 50 }
  const direct = findPlacementOptions(grid, targetWordA.text, targetWordB.text, directOpts)
  for (const target of direct) {
    collected.push({ helpers: [], target })
  }

  // Depth 1..maxDepth — backward chains.
  for (let depth = 1; depth <= maxDepth; depth++) {
    const result = findSolutionsAtDepthDetailed(
      grid, targetWordA, targetWordB, words, pairings, depth, options,
    )
    if (result.truncated) truncated = true
    for (const sol of result.solutions) collected.push(sol)
  }

  return { solutions: rankSolutions(collected), truncated }
}
